import { SubjectLocation } from "../../../_objects/subject-model";
import { SubjectModelLocationsSchema } from "../../../_offline/models/subjectModel-locations.schema";
import { SubjectModelSchema } from "../../../_offline/models/subjectModel.schema";
import {
  LocationNode,
  AssetType,
  AssetTypeProperty,
  AssetTypeRelation,
  AssetTypeCategory,
  CategoryDict,
  LocationNameToCategory,
  AssetTypeLocation,
  CreatorName as CreatorName,
  AssetTypeExportResult,
  AssetTypeStructureImage,
  SerialNumberProperty
} from "./types";
import { v4 as uuidv4 } from "uuid";

const ReferenceNotePropertyNamePrefix = "Reference Note";
const PropertyTypeText = 0;

export function processLocations(
  locations: LocationNode[],
  previous: AssetType,
  assetTypes: AssetType[],
  relations: AssetTypeRelation[],
  properties: AssetTypeProperty[],
  referenceNoteProperties: AssetTypeProperty["Property"][],
  rootName: string
): void {
  locations.forEach((location, index) => {
    const assetType = toAssetType(location, previous, rootName);
    assetTypes.push(assetType);
    relations.push({
      ParentId: previous.Id,
      ChildId: assetType.Id,
      Occurence: 1,
      ChildLabel: "",
      Location: rectToLocation(location.rectInStructImage),
      Position: index
    });

    addReferenceNotes(location, assetType, properties, referenceNoteProperties);

    processLocations(location.children, assetType, assetTypes, relations, properties, referenceNoteProperties, rootName);
  });
}

function addReferenceNotes(
  location: LocationNode,
  assetType: AssetType,
  properties: AssetTypeProperty[],
  referenceNoteProperties: AssetTypeProperty["Property"][]
): void {
  const notes = location.inspectionFor;
  if (!notes?.length) return;

  notes.forEach((value, i) => {
    properties.push({
      Property: referenceNoteProperties[i],
      AssetTypeId: assetType.Id,
      Value: value,
      IsMandatory: false,
      IsInherited: true,
      IsReadOnly: false
    });
  });
}

function getMaxReferenceNoteCount(nodes: LocationNode[]): number {
  return nodes.reduce((max, node) => Math.max(max, node.inspectionFor?.length ?? 0, getMaxReferenceNoteCount(node.children)), 0);
}

function buildReferenceNoteProperties(maxCount: number): AssetTypeProperty["Property"][] {
  return Array.from({ length: maxCount }, (_, i) => ({
    Name: `${ReferenceNotePropertyNamePrefix} ${i + 1}`,
    Id: uuidv4(),
    Type: PropertyTypeText,
    IsSystem: false
  }));
}

function stripSpaces(value: string): string {
  return value.replace(/\s+/g, "");
}

function toAssetType(node: LocationNode, previous: AssetType, rootName: string): AssetType {
  const uniqueName = previous ? `${previous.Name}_${node.abbreviation}` : `${rootName}_${node.abbreviation}`;

  return {
    Id: uuidv4(),
    Name: uniqueName,
    ShortName: stripSpaces(node.abbreviation),
    DisplayName: node.name,
    Category: mapCategory(node.name, node.isDataPlate),
    Description: node.description,
    Creator: CreatorName,
    IsEnabled: previous.IsEnabled
  };
}

export function mapCategory(name: string, isDataplate: boolean): AssetTypeCategory | null {
  if (isDataplate) {
    const cat = CategoryDict["dataplate"];
    return { Id: cat.id, Name: "dataplate", IconName: cat.iconName };
  }

  const categoryKey = LocationNameToCategory[name];
  if (!categoryKey) return null;

  const cat = CategoryDict[categoryKey];
  if (!cat) return null;

  return { Id: cat.id, Name: categoryKey, IconName: cat.iconName };
}

export function rectToLocation(rect: LocationNode["rectInStructImage"]): AssetTypeLocation | null {
  if (!rect) return null;
  const { top, left, width, height } = rect;
  if (top == null || left == null || width == null || height == null || (top === 0 && left === 0 && width === 0 && height === 0)) {
    return null;
  }
  const t = top / 100;
  const l = left / 100;
  const b = (top + height) / 100;
  const r = (left + width) / 100;
  return {
    Points: [
      { X: l, Y: t },
      { X: r, Y: t },
      { X: r, Y: b },
      { X: l, Y: b }
    ]
  };
}

export function toAssetTypeExport(
  subjectModel: SubjectModelSchema,
  locations: SubjectModelLocationsSchema[],
  structImagePath: string | null
): AssetTypeExportResult {
  const assetTypes: AssetType[] = [];
  const relations: AssetTypeRelation[] = [];
  const properties: AssetTypeProperty[] = [];
  const structImageEntries: AssetTypeStructureImage[] = [];

  const rootId = uuidv4();
  assetTypes.push({
    Id: rootId,
    Name: subjectModel.name,
    ShortName: stripSpaces(subjectModel.name),
    DisplayName: subjectModel.name,
    Category: mapCategory(subjectModel.name, false),
    Description: `${subjectModel.name} Migrated from Inspection template`,
    Creator: CreatorName,
    IsEnabled: subjectModel.isValid
  });

  properties.push({
    Property: SerialNumberProperty,
    AssetTypeId: rootId,
    Value: "",
    IsMandatory: true,
    IsInherited: true,
    IsReadOnly: false
  });

  if (structImagePath) {
    structImageEntries.push({
      FileName: "struct.jpg",
      ContentType: "image/jpeg",
      AssetTypeId: rootId,
      SourceFilePath: structImagePath
    });
  }

  const newestLoc = locations.find((l) => l.isNewestVersion) ?? locations[0];
  const nodes: SubjectLocation[] = JSON.parse(newestLoc.locations ?? "[]");
  const referenceNoteProperties = buildReferenceNoteProperties(getMaxReferenceNoteCount(nodes));
  processLocations(nodes, assetTypes[0], assetTypes, relations, properties, referenceNoteProperties, subjectModel.name);

  return {
    export: {
      AssetTypes: assetTypes,
      AssetTypeRelations: relations,
      AssetTypeProperties: properties,
      AssetTypeStructureImages: structImageEntries,
      AssetTypeResources: []
    },
    structImageEntries
  };
}
